/**
 * Vitamin concept-test segments service.
 *
 * Fetches the segment / sub-segment breakdown for a population from the backend
 * proxy (`GET /vitamin/populations/{population}/segments`), which in turn proxies
 * the external population service. Used by the concept-test form to let users
 * narrow a run to specific segments / sub-segments.
 */

const API_URL = import.meta.env.VITE_API_URL || '';
const CACHE_TTL_MS = 5 * 60 * 1000;

export interface VitaminSubSegment {
  sub_segment_id: number;
  sub_segment_name: string;
  sub_segment_description?: string;
  agent_count: number;
}

export interface VitaminSegment {
  segment_id: number;
  segment_name: string;
  segment_description?: string;
  agent_count: number;
  sub_segments: VitaminSubSegment[];
}

export interface PopulationSegments {
  population: string;
  total_agents: number;
  segments: VitaminSegment[];
}

interface CacheEntry {
  data: PopulationSegments;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<PopulationSegments>>();

function normalize(raw: unknown, population: string): PopulationSegments {
  const data = (raw || {}) as Partial<PopulationSegments>;
  const segments = Array.isArray(data.segments) ? data.segments : [];
  return {
    population: data.population || population,
    total_agents: typeof data.total_agents === 'number' ? data.total_agents : 0,
    segments: segments.map((s) => ({
      ...s,
      sub_segments: Array.isArray(s?.sub_segments) ? s.sub_segments : [],
    })),
  };
}

/**
 * Fetch segments for a population. Returns a normalized payload; a population with
 * no segments resolves to `{ segments: [] }` (caller falls back to defaults).
 * De-duplicates concurrent requests and caches results for a few minutes.
 */
export async function fetchPopulationSegments(population: string): Promise<PopulationSegments> {
  if (!population) return { population, total_agents: 0, segments: [] };

  const cached = cache.get(population);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) return cached.data;

  const existing = inFlight.get(population);
  if (existing) return existing;

  const request = (async () => {
    const resp = await fetch(
      `${API_URL}/vitamin/populations/${encodeURIComponent(population)}/segments`,
      { credentials: 'include' },
    );
    if (!resp.ok) throw new Error(`Failed to fetch segments (${resp.status})`);
    const data = normalize(await resp.json(), population);
    cache.set(population, { data, timestamp: Date.now() });
    return data;
  })();

  inFlight.set(population, request);
  try {
    return await request;
  } finally {
    inFlight.delete(population);
  }
}
